import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { RuntimeConfigService } from "../../../../services/runtime-config.service";
import { PortalLinkService } from "../../../../services/portal-link.service";
import { ServiceStatus } from "../service-status.enum";
import { FormCompletionTodoData, TodoCard, TodoItem, VopSurveyTodoData } from "../../student/models/Todo";
import { RtI18nDirective } from "../runtime-translation.directive";
import { TranslatePipe } from "@ngx-translate/core";

/**
 * The Parent Portal "To Dos" card list (header + badge + cards), shared by the main-menu
 * sidebar and the student dashboard. Dumb component: the host loads the todos (and decides
 * any per-student filtering) and passes them in; this component only flattens and renders.
 */
@Component({
    selector: 'app-todos-section',
    standalone: true,
    templateUrl: './todos-section.component.html',
    styleUrl: './todos-section.component.scss',
    imports: [CommonModule, MatProgressSpinnerModule, RtI18nDirective, TranslatePipe]
})
export class TodosSectionComponent {

  readonly ServiceStatus = ServiceStatus;

  /** Flattened cards the template renders, rebuilt whenever the todos input changes. */
  public cards: TodoCard[] = [];

  /** Load state of the host's todos fetch; the section shows a spinner until it settles. */
  @Input() status: ServiceStatus = ServiceStatus.unset;

  /** Whether the re-enrollment card shows at the top of the list. */
  @Input() showReenrollment = false;

  /** VOP survey cards render but are inert while a staff member impersonates the guardian. */
  @Input() isImpersonating = false;

  @Input() set todos(value: TodoItem[] | null) {
    this.cards = this.buildTodoCards(value ?? []);
  }

  constructor(
    private runtimeConfig: RuntimeConfigService,
    public portalLink: PortalLinkService
  ) { }

  /** The Scholar parent portal's per-student re-enrollment decision form. */
  public get reenrollmentUrl(): string {
    return this.runtimeConfig.portalUrl('reenrollment');
  }

  /** What the header badge counts: the todo cards plus the re-enrollment card when active. */
  public get todoCount(): number {
    return this.cards.length + (this.showReenrollment ? 1 : 0);
  }

  /**
   * The section shows while the fetch is in flight (header + spinner) and whenever there's
   * something to list; once loading settles with nothing to show, the whole section —
   * header included — disappears.
   */
  public get showSection(): boolean {
    return this.status === ServiceStatus.loading ||
      this.status === ServiceStatus.unset ||
      this.todoCount > 0;
  }

  /** [0..n-1], for rendering one progress segment per form in the template. */
  public range(n: number): number[] {
    return Array.from({ length: n }, (_, i) => i);
  }

  /**
   * Flattens the server's todos into renderable cards, in server order (VOP surveys first,
   * then form todos). Done todos never render — they vanish on their own once the underlying
   * condition resolves; there is no read/dismiss state to persist.
   */
  private buildTodoCards(todos: TodoItem[]): TodoCard[] {
    const cards: TodoCard[] = [];

    for (const todo of todos ?? []) {
      if (todo.IsDone) continue;

      if (todo.ComponentKey === 'formCompletionTodo') {
        const data = todo.Data as FormCompletionTodoData | null;
        if (!data) continue;
        // Order 2 is "Required Form Completion"; order 1 (and anything else) is registration.
        cards.push({
          kind: 'form',
          formsKind: todo.OrderBy?.Order === 2 ? 'required' : 'registration',
          yearLabel: this.shortYearLabel(data.academicYearName),
          schoolName: data.schoolName,
          studentFirstName: data.studentName?.split(' ')[0],
          formsCompleted: data.formsCompleted,
          totalForms: data.totalForms,
          // Forms are completed in the Scholar parent portal (same flow as the seat-accept
          // deep link) — School Connect has no native forms page.
          link: this.runtimeConfig.portalUrl(`student/${data.studentID}/${data.studentInSchoolID}/forms`)
        });
      } else if (todo.ComponentKey === 'voiceOfTheParentTodo') {
        const data = todo.Data as VopSurveyTodoData | null;
        if (!data?.surveyLink) continue;
        cards.push({
          kind: 'vop',
          title: todo.Title ?? 'Voice of the Parent survey',
          schoolName: data.schoolName,
          link: data.surveyLink
        });
      } else {
        // Unknown componentKey (future todo types): basic title/description card, no link.
        cards.push({
          kind: 'basic',
          title: todo.Title,
          message: todo.Description
        });
      }
    }

    return cards;
  }

  /** "2026-2027" (the portal's year format) or "2026-27" → "'26–27"; falls back to the raw name. */
  private shortYearLabel(yearName?: string): string {
    const match = yearName?.match(/^\d{2}(\d{2})-(?:\d{2})?(\d{2})$/);
    return match ? `'${match[1]}–${match[2]}` : (yearName ?? '');
  }
}
